import type {
  ConnectionType,
  EffectiveStatus,
  LinkState,
  NetworkNode,
  NodeKind,
  SimBinding,
} from "@/features/network-topology/types";
import type { SimPermissions } from "@/features/simulation/types";

/**
 * ネットワークトポロジの純粋関数群。
 *
 * 責務:
 *  - ステータス伝播 (manual + 親チェーン → effective)
 *  - 権限導出 (network → SimPermissions)
 *  - ツリー操作 (add/remove/reparent) と整合性チェック
 *  - 未使用の id 生成
 *
 * React/Zustand/Pixi 不使用。テストから直接呼べる。
 */

/** すべてのノードの effective status を一括計算する。 */
export function computeEffectiveStatus(nodes: readonly NetworkNode[]): Map<string, EffectiveStatus> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const memo = new Map<string, EffectiveStatus>();

  function visit(id: string): EffectiveStatus {
    const cached = memo.get(id);
    if (cached) return cached;
    const node = byId.get(id);
    if (!node) {
      const v: EffectiveStatus = "unreachable";
      memo.set(id, v);
      return v;
    }

    // 自分自身の意図が offline / connecting なら最優先（fault より stopped を優先）
    if (node.manual === "offline") {
      memo.set(id, "offline");
      return "offline";
    }
    if (node.manual === "connecting") {
      memo.set(id, "connecting");
      return "connecting";
    }

    // 親チェーンを辿る
    if (node.parentId !== null) {
      // 親リンクが手動切断されていれば自分は unreachable（リンクが断 → 上流到達不能）
      if (node.linkDisconnected === true) {
        memo.set(id, "unreachable");
        return "unreachable";
      }
      const parent = visit(node.parentId);
      // 上流が offline / unreachable / connecting なら自分は unreachable
      if (parent === "offline" || parent === "unreachable" || parent === "connecting") {
        memo.set(id, "unreachable");
        return "unreachable";
      }
      // 上流が fault でも自分は通信できているので fault/online は自身の faulted で決まる
    }

    // online チェーンに乗っていれば、faulted で fault / online を分岐
    const status: EffectiveStatus = node.faulted ? "fault" : "online";
    memo.set(id, status);
    return status;
  }

  for (const n of nodes) {
    visit(n.id);
  }
  return memo;
}

/**
 * 親 → 子のリンクの表示状態を決定する。
 *  - childLinkDisconnected=true → "disconnected"（手動切断、破線）
 *  - いずれかが fault           → "fault"（オレンジ点滅）
 *  - 両端 online                 → "normal"（緑・流れアニメ）
 *  - それ以外（停止系）           → "stopped"（薄色・無アニメ）
 */
export function computeLinkState(
  parent: EffectiveStatus,
  child: EffectiveStatus,
  childLinkDisconnected: boolean = false,
): LinkState {
  if (childLinkDisconnected) return "disconnected";
  if (parent === "fault" || child === "fault") return "fault";
  if (parent === "online" && child === "online") return "normal";
  return "stopped";
}

/**
 * sim へ渡す権限フラグを計算する。
 *
 * 通信は成立しているが異常検知中 ("fault") の機器は sim を停止しない
 * — 工場側はとりあえず動かしながら可視化で異常を伝える、というデモ前提。
 * "online" または "fault" のいずれかなら enabled。
 */
export function deriveSimPermissions(nodes: readonly NetworkNode[]): SimPermissions {
  const status = computeEffectiveStatus(nodes);

  const enabledFor = (kind: SimBinding["kind"]): boolean => {
    for (const n of nodes) {
      if (n.simBinding?.kind === kind) {
        const s = status.get(n.id);
        if (s === "online" || s === "fault") return true;
      }
    }
    return false;
  };

  return {
    pressEnabled: enabledFor("press"),
    armEnabled: enabledFor("arm"),
    transportEnabled: enabledFor("transport"),
    spawnEnabled: enabledFor("spawn"),
  };
}

/** 指定ノードの直接の子を取得 */
export function getChildren(nodes: readonly NetworkNode[], parentId: string): NetworkNode[] {
  return nodes.filter((n) => n.parentId === parentId);
}

/** 指定ノードを含む配下の subtree（自身 + 子孫）の id 集合 */
export function getDescendants(nodes: readonly NetworkNode[], rootId: string): Set<string> {
  const result = new Set<string>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined) break;
    for (const child of nodes) {
      if (child.parentId === id && !result.has(child.id)) {
        result.add(child.id);
        stack.push(child.id);
      }
    }
  }
  return result;
}

/** 親 → 子 への reparent が循環を作らないか判定 */
export function isReparentSafe(nodes: readonly NetworkNode[], nodeId: string, newParentId: string | null): boolean {
  if (nodeId === newParentId) return false;
  if (newParentId === null) return true;
  const descendants = getDescendants(nodes, nodeId);
  return !descendants.has(newParentId);
}

/** 既存 id と衝突しない id を生成（プレフィックス + 連番） */
export function nextNodeId(nodes: readonly NetworkNode[], prefix: string): string {
  const existing = new Set(nodes.map((n) => n.id));
  let i = 1;
  while (existing.has(`${prefix}-${i}`)) i += 1;
  return `${prefix}-${i}`;
}

/**
 * 新規ノードを追加する。
 * parentId が存在しない、または kind 制約違反の場合は元配列を返す。
 * 現状は kind 制約を緩く採用（NODE_KIND_META は将来用）。
 *
 * `spec.id` を渡すと明示 id を使用 (P5: editor 連携で `sim-<objectId>` 形式で同期)。
 * 既存 id と衝突する場合は null を返す。
 */
export function addNode(
  nodes: readonly NetworkNode[],
  spec: {
    kind: NodeKind;
    label: string;
    parentId: string;
    simBinding?: SimBinding | null;
    /** 明示 id を指定 (省略時は kind+連番で自動採番) */
    id?: string;
    /** 親ノードへのリンク種別 (P3 機能・未指定なら rj45) */
    linkType?: import("@/features/network-topology/types").ConnectionType;
  },
): { nodes: NetworkNode[]; addedId: string } | null {
  const parent = nodes.find((n) => n.id === spec.parentId);
  if (!parent) return null;
  let id: string;
  if (spec.id) {
    if (nodes.find((n) => n.id === spec.id)) return null;
    id = spec.id;
  } else {
    id = nextNodeId(nodes, spec.kind);
  }
  const newNode: NetworkNode = {
    id,
    kind: spec.kind,
    label: spec.label,
    parentId: spec.parentId,
    manual: "online",
    simBinding: spec.simBinding ?? null,
    ...(spec.linkType ? { linkType: spec.linkType } : {}),
  };
  return { nodes: [...nodes, newNode], addedId: id };
}

/**
 * ノードを削除する（配下の子孫も同時に削除）。
 * root ノード (parentId: null) は削除不可で元配列を返す。
 */
export function removeNode(nodes: readonly NetworkNode[], nodeId: string): NetworkNode[] {
  const target = nodes.find((n) => n.id === nodeId);
  if (!target || target.parentId === null) return [...nodes];
  const removeIds = getDescendants(nodes, nodeId);
  return nodes.filter((n) => !removeIds.has(n.id));
}

/** 単一ノードの manual 状態のみ書き換える純粋関数 */
export function setManual(nodes: readonly NetworkNode[], nodeId: string, manual: NetworkNode["manual"]): NetworkNode[] {
  return nodes.map((n) => (n.id === nodeId ? { ...n, manual } : n));
}

/** sim active フラグを書き換える純粋関数（sim → network 反映用） */
export function setSimActive(
  nodes: readonly NetworkNode[],
  binding: SimBinding["kind"],
  active: boolean,
): NetworkNode[] {
  return nodes.map((n) => (n.simBinding?.kind === binding ? { ...n, simActive: active } : n));
}

/** 単一ノードの faulted フラグを書き換える純粋関数 */
export function setFaulted(nodes: readonly NetworkNode[], nodeId: string, faulted: boolean): NetworkNode[] {
  return nodes.map((n) => (n.id === nodeId ? { ...n, faulted } : n));
}

/**
 * 子ノードと親を結ぶリンクの切断フラグを書き換える純粋関数。
 * `linkDisconnected=true` で子は computeEffectiveStatus 上 unreachable 扱い。
 * root (parentId=null) には適用しない（リンクが存在しないため元配列を返す）。
 */
export function setLinkDisconnected(
  nodes: readonly NetworkNode[],
  childNodeId: string,
  disconnected: boolean,
): NetworkNode[] {
  return nodes.map((n) => {
    if (n.id !== childNodeId) return n;
    if (n.parentId === null) return n;
    return { ...n, linkDisconnected: disconnected };
  });
}

/** リンク (子側に保存) の接続方式を変更する純粋関数 */
export function setLinkType(
  nodes: readonly NetworkNode[],
  childNodeId: string,
  linkType: ConnectionType,
): NetworkNode[] {
  return nodes.map((n) => {
    if (n.id !== childNodeId) return n;
    if (n.parentId === null) return n;
    return { ...n, linkType };
  });
}

/** すべてのノードの faulted を一括クリアする純粋関数 */
export function clearAllFaults(nodes: readonly NetworkNode[]): NetworkNode[] {
  return nodes.map((n) => (n.faulted ? { ...n, faulted: false } : n));
}

/**
 * 障害注入の候補となるノード id 配列を返す。
 *  - root チェーン（internet/gateway/l3_switch）と既に offline / fault のノードは除外
 *  - 結果が空ならデモが何もできないので、l2_switch まで広げてフォールバック
 */
export function pickFaultCandidates(nodes: readonly NetworkNode[]): string[] {
  const exclude = new Set<NodeKind>(["internet", "gateway"]);
  const primary = nodes.filter((n) => !exclude.has(n.kind) && n.manual === "online" && !n.faulted);
  if (primary.length > 0) return primary.map((n) => n.id);
  return nodes.filter((n) => n.manual === "online" && !n.faulted && n.kind !== "internet").map((n) => n.id);
}
